import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useForm } from "react-hook-form";
import { useNotification, useTranslate } from "@refinedev/core";

import { Form } from "@shared/components";
import { AgreementsAutocomplete } from "@shared/components/AutocompleteFields";
import { CategoryMedal } from "@modules/financial/components/CategoryMedal";
import { formatFinancialError } from "@modules/financial/nfe";
import {
  deleteVolumetryEmailFileService,
  getVolumetryAgreementHistoryService,
  getVolumetryEmailHistoryService,
  openVolumetryEmailService,
  patchVolumetryEmailFileService,
  patchVolumetryEmailService,
  refreshVolumetryEmailService,
  resetVolumetryEmailService,
  sendVolumetryEmailService,
} from "@modules/financial/services";
import {
  VolumetryAgreementHistoryEvent,
  VolumetryEmail,
  VolumetryEmailFile,
  VolumetryEmailHistoryEvent,
  VolumetryEmailPatch,
  VolumetryEmailRow,
} from "@modules/financial/types";

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

interface PeriodValues {
  agreement_id: number | null;
  year: string;
  month: string;
}

interface DraftForm {
  to_emails: string;
  cc_emails: string;
  subject: string;
  body: string;
}

type UploadField = "images" | "attachments" | "table_image" | "attachment";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LIGHT_ROW: VolumetryEmailRow = {
  key: "light",
  plan: "LIGHT",
  total: 0,
  fill: "F2F2F2",
  font: "000000",
};

function rowsWithLight(rows: VolumetryEmailRow[]) {
  if (rows.length === 0) return rows;
  const hasLight = rows.some(
    (row) => row.key === "light" || row.plan.trim().toLowerCase() === "light"
  );
  if (hasLight) return rows;
  return [LIGHT_ROW, ...rows];
}

const FALLBACK_TABLE_TAG = "{{volumetria}}";
const FALLBACK_SIGNATURE_TAG = "{{assinatura}}";
const SIGNATURE_SRC = "/financial/assinatura-financeiro.jpg";

function parseEmailList(value: string) {
  const seen = new Set<string>();
  const emails: string[] = [];
  value.split(/[;,]/).forEach((item) => {
    const email = item.trim();
    const key = email.toLowerCase();
    if (!email || seen.has(key)) return;
    seen.add(key);
    emails.push(email);
  });
  return emails;
}

function joinEmailList(emails: string[]) {
  return emails.join(", ");
}

function sharedEmails(toValue: string, ccValue: string) {
  const copies = new Set(parseEmailList(ccValue).map((email) => email.toLowerCase()));
  return parseEmailList(toValue).filter((email) => copies.has(email.toLowerCase()));
}

function yearOptions() {
  const current = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, index) => current - 4 + index);
}

function toForm(email: VolumetryEmail): DraftForm {
  return {
    to_emails: email.to_emails ?? "",
    cc_emails: email.cc_emails ?? "",
    subject: email.subject ?? "",
    body: email.body ?? "",
  };
}

function isDirty(email: VolumetryEmail, form: DraftForm) {
  return (
    form.to_emails !== (email.to_emails ?? "") ||
    form.cc_emails !== (email.cc_emails ?? "") ||
    form.subject !== (email.subject ?? "") ||
    form.body !== (email.body ?? "")
  );
}

function filesByRole(files: VolumetryEmailFile[] | undefined, role: VolumetryEmailFile["role"]) {
  return [...(files ?? [])]
    .filter((file) => file.role === role)
    .sort((left, right) => left.position - right.position || left.id - right.id);
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function splitBody(body: string, tags: string[]) {
  const unique = [...new Set(tags.filter(Boolean))];
  if (unique.length === 0) return [body];
  return body.split(new RegExp(`(${unique.map(escapeRegExp).join("|")})`, "g"));
}

export function VolumetryEmailPanel() {
  const translate = useTranslate();
  const { open } = useNotification();
  const now = new Date();
  const methods = useForm<PeriodValues>({
    defaultValues: {
      agreement_id: null,
      year: String(now.getFullYear()),
      month: String(now.getMonth() + 1),
    },
  });

  const [email, setEmail] = useState<VolumetryEmail | null>(null);
  const [form, setForm] = useState<DraftForm | null>(null);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<VolumetryEmailFile | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<VolumetryEmailHistoryEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [agreementHistoryOpen, setAgreementHistoryOpen] = useState(false);
  const [agreementHistory, setAgreementHistory] = useState<VolumetryAgreementHistoryEvent[]>([]);
  const [agreementHistoryLoading, setAgreementHistoryLoading] = useState(false);
  const [agreementHistoryError, setAgreementHistoryError] = useState("");
  const agreementId = methods.watch("agreement_id");
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const busy = opening || saving || refreshing || sending || resetting || fileBusy;
  const dirty = email && form ? isDirty(email, form) : false;

  const applyEmail = (next: VolumetryEmail, keepDraft = false) => {
    setEmail(next);
    setForm((current) => (keepDraft && current ? current : toForm(next)));
  };

  const insertAtCursor = (tag: string) => {
    if (!form || !tag) return;
    const field = bodyRef.current;
    const start = field?.selectionStart ?? form.body.length;
    const end = field?.selectionEnd ?? form.body.length;
    const next = `${form.body.slice(0, start)}${tag}${form.body.slice(end)}`;
    const cursor = start + tag.length;
    setForm({ ...form, body: next });
    window.setTimeout(() => {
      const node = bodyRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(cursor, cursor);
    }, 0);
  };

  const openPreview = async (values: PeriodValues) => {
    if (!values.agreement_id || !values.year || !values.month) {
      setError(translate("financial_admin.volumetry.period_required"));
      return;
    }
    setOpening(true);
    setError("");
    try {
      const next = await openVolumetryEmailService({
        agreement_id: values.agreement_id,
        year: Number(values.year),
        month: Number(values.month),
      });
      applyEmail(next);
    } catch (caught) {
      setError(formatFinancialError(caught, translate("financial_admin.volumetry.open_failed")));
    } finally {
      setOpening(false);
    }
  };

  const save = async () => {
    if (!email || !form) return;
    if (sharedEmails(form.to_emails, form.cc_emails).length > 0) {
      setError(translate("financial_admin.volumetry.email_in_both"));
      return;
    }
    const changes: VolumetryEmailPatch = {};
    if (form.to_emails !== (email.to_emails ?? "")) changes.to_emails = form.to_emails;
    if (form.cc_emails !== (email.cc_emails ?? "")) changes.cc_emails = form.cc_emails;
    if (form.subject !== (email.subject ?? "")) changes.subject = form.subject;
    if (form.body !== (email.body ?? "")) changes.body = form.body;
    if (Object.keys(changes).length === 0) {
      open?.({ type: "success", message: translate("financial_admin.volumetry.nothing_to_save") });
      return;
    }

    setSaving(true);
    setError("");
    try {
      const next = await patchVolumetryEmailService(email.id, changes);
      applyEmail(next);
      open?.({ type: "success", message: translate("financial_admin.volumetry.saved") });
    } catch (caught) {
      setError(formatFinancialError(caught, translate("financial_admin.volumetry.save_failed")));
    } finally {
      setSaving(false);
    }
  };

  const refreshNumbers = async () => {
    if (!email || dirty) return;
    setRefreshing(true);
    setError("");
    try {
      const next = await refreshVolumetryEmailService(email.id);
      applyEmail(next);
      open?.({ type: "success", message: translate("financial_admin.volumetry.refreshed") });
    } catch (caught) {
      setError(formatFinancialError(caught, translate("financial_admin.volumetry.refresh_failed")));
    } finally {
      setRefreshing(false);
    }
  };

  const resetEmail = async () => {
    if (!email) return;
    setConfirmReset(false);
    setResetting(true);
    setError("");
    try {
      const next = await resetVolumetryEmailService(email.id);
      applyEmail(next);
      open?.({ type: "success", message: translate("financial_admin.volumetry.reset_done") });
    } catch (caught) {
      setError(formatFinancialError(caught, translate("financial_admin.volumetry.reset_failed")));
    } finally {
      setResetting(false);
    }
  };

  const sendEmail = async () => {
    if (!email || dirty) return;
    setConfirmSend(false);
    setSending(true);
    setError("");
    try {
      const next = await sendVolumetryEmailService(email.id);
      applyEmail(next);
      open?.({ type: "success", message: translate("financial_admin.volumetry.sent") });
    } catch (caught) {
      setError(formatFinancialError(caught, translate("financial_admin.volumetry.send_failed")));
    } finally {
      setSending(false);
    }
  };

  const uploadFiles = async (field: UploadField, list: FileList | null) => {
    if (!email || !list?.length) return;
    const data = new FormData();
    Array.from(list).forEach((file) => data.append(field, file));
    setFileBusy(true);
    setError("");
    try {
      applyEmail(await patchVolumetryEmailService(email.id, data), dirty);
    } catch (caught) {
      setError(formatFinancialError(caught, translate("financial_admin.volumetry.file_failed")));
    } finally {
      setFileBusy(false);
    }
  };

  const setFileInclude = async (file: VolumetryEmailFile, include: boolean) => {
    if (!email) return;
    setFileBusy(true);
    setError("");
    try {
      applyEmail(await patchVolumetryEmailFileService(email.id, file.id, include), dirty);
    } catch (caught) {
      setError(formatFinancialError(caught, translate("financial_admin.volumetry.file_failed")));
    } finally {
      setFileBusy(false);
    }
  };

  const deleteFile = async () => {
    if (!email || !fileToDelete) return;
    setFileBusy(true);
    setError("");
    try {
      applyEmail(await deleteVolumetryEmailFileService(email.id, fileToDelete.id), dirty);
      setFileToDelete(null);
    } catch (caught) {
      setError(formatFinancialError(caught, translate("financial_admin.volumetry.file_failed")));
    } finally {
      setFileBusy(false);
    }
  };

  const openAgreementHistory = async () => {
    if (!agreementId) {
      setError(translate("financial_admin.volumetry.agreement_required"));
      return;
    }
    setAgreementHistoryOpen(true);
    setAgreementHistoryLoading(true);
    setAgreementHistoryError("");
    try {
      setAgreementHistory(await getVolumetryAgreementHistoryService(agreementId));
    } catch (caught) {
      setAgreementHistoryError(
        formatFinancialError(caught, translate("financial_admin.volumetry.history_failed"))
      );
    } finally {
      setAgreementHistoryLoading(false);
    }
  };

  const openHistory = async () => {
    if (!email) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError("");
    try {
      setHistory(await getVolumetryEmailHistoryService(email.id));
    } catch (caught) {
      setHistoryError(formatFinancialError(caught, translate("financial_admin.volumetry.history_failed")));
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
        <Form methods={methods} onSubmit={openPreview}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" }}>
            <Box sx={{ width: { xs: "100%", sm: 320 }, "& .MuiAutocomplete-root": { width: "100%" } }}>
              <AgreementsAutocomplete
                name="agreement_id"
                label={translate("financial_admin.filters.agreement")}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 180 } }}>
              <Form.Select
                name="month"
                label={translate("financial_admin.filters.month")}
                options={MONTHS.map((month) => ({
                  value: String(month),
                  label: translate(`financial_admin.months.${month}`),
                }))}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 140 } }}>
              <Form.Select
                name="year"
                label={translate("financial_admin.filters.year")}
                options={yearOptions().map((year) => ({ value: String(year), label: String(year) }))}
              />
            </Box>
            <Button type="submit" variant="contained" disabled={busy} sx={{ height: 45, minWidth: 140 }}>
              {translate("financial_admin.volumetry.open")}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="warning"
              disabled={busy || !email}
              sx={{ height: 45 }}
              onClick={() => setConfirmReset(true)}
            >
              {translate("financial_admin.volumetry.reset")}
            </Button>
            <Button
              type="button"
              variant="outlined"
              disabled={busy || !email}
              sx={{ height: 45 }}
              onClick={() => void openHistory()}
            >
              {translate("financial_admin.volumetry.history_month")}
            </Button>
            <Button
              type="button"
              variant="outlined"
              disabled={busy || !agreementId}
              sx={{ height: 45 }}
              onClick={() => void openAgreementHistory()}
            >
              {translate("financial_admin.volumetry.agreement_history")}
            </Button>
          </Box>
        </Form>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      {email && form && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {email.sent_at
                ? `${translate("financial_admin.volumetry.sent_at")} ${new Date(email.sent_at).toLocaleString()}`
                : translate("financial_admin.volumetry.never_sent")}
            </Typography>
            {email.rebuilt_at && (
              <Typography variant="body2" color="text.secondary">
                {translate("financial_admin.volumetry.rebuilt_at")} {new Date(email.rebuilt_at).toLocaleString()}
              </Typography>
            )}
          </Box>

          {email.warnings.map((warning) => (
            <Alert key={warning} severity="warning" sx={{ mb: 1.5 }}>
              {warning}
            </Alert>
          ))}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <EmailListField
              label={translate("financial_admin.volumetry.to")}
              value={form.to_emails}
              otherValue={form.cc_emails}
              otherMessage={translate("financial_admin.volumetry.email_in_cc")}
              tone="to"
              onChange={(to_emails) => setForm({ ...form, to_emails })}
            />
            <EmailListField
              label={translate("financial_admin.volumetry.cc")}
              value={form.cc_emails}
              otherValue={form.to_emails}
              otherMessage={translate("financial_admin.volumetry.email_in_to")}
              tone="cc"
              onChange={(cc_emails) => setForm({ ...form, cc_emails })}
            />
            <TextField
              label={translate("financial_admin.volumetry.subject")}
              value={form.subject}
              onChange={(event) => setForm({ ...form, subject: event.target.value })}
              fullWidth
            />
            <Box>
              <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy}
                  onClick={() =>
                    insertAtCursor(email.placeholders?.table || FALLBACK_TABLE_TAG)
                  }
                >
                  {translate("financial_admin.volumetry.insert_table")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy}
                  onClick={() =>
                    insertAtCursor(email.placeholders?.signature || FALLBACK_SIGNATURE_TAG)
                  }
                >
                  {translate("financial_admin.volumetry.insert_signature")}
                </Button>
              </Box>
              <TextField
                label={translate("financial_admin.volumetry.body")}
                value={form.body}
                inputRef={bodyRef}
                onChange={(event) => setForm({ ...form, body: event.target.value })}
                helperText={translate("financial_admin.volumetry.body_hint")}
                multiline
                minRows={8}
                fullWidth
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {translate("financial_admin.volumetry.preview")}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fff", color: "#222" }}>
                <EmailBodyPreview
                  body={form.body}
                  tableTag={email.placeholders?.table || FALLBACK_TABLE_TAG}
                  signatureTag={email.placeholders?.signature || FALLBACK_SIGNATURE_TAG}
                  tableFile={filesByRole(email.images, "table")[0]}
                  extras={filesByRole(email.images, "extra")}
                />
              </Paper>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <FileColumn
                title={translate("financial_admin.volumetry.image")}
                hint={translate("financial_admin.volumetry.images_hint")}
                generated={filesByRole(email.images, "table")[0]}
                extras={filesByRole(email.images, "extra")}
                emptyLabel={translate("financial_admin.volumetry.image_empty")}
                replaceLabel={translate("financial_admin.volumetry.replace_image")}
                addLabel={translate("financial_admin.volumetry.add_images")}
                replaceField="table_image"
                addField="images"
                replaceAccept="image/*"
                addAccept="image/*"
                disabled={busy}
                onUpload={uploadFiles}
                onInclude={setFileInclude}
                onDelete={setFileToDelete}
              />
              <FileColumn
                title={translate("financial_admin.volumetry.attachment")}
                hint={translate("financial_admin.volumetry.attachments_hint")}
                generated={filesByRole(email.attachments, "spreadsheet")[0]}
                extras={filesByRole(email.attachments, "extra")}
                emptyLabel={translate("financial_admin.volumetry.attachment_empty")}
                replaceLabel={translate("financial_admin.volumetry.replace_attachment")}
                addLabel={translate("financial_admin.volumetry.add_attachments")}
                replaceField="attachment"
                addField="attachments"
                disabled={busy}
                onUpload={uploadFiles}
                onInclude={setFileInclude}
                onDelete={setFileToDelete}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2">
                {translate("financial_admin.volumetry.rows")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {translate("financial_admin.volumetry.rows_hint")}
              </Typography>
              <Box
                sx={{
                  maxWidth: 420,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{translate("financial_admin.volumetry.plan")}</TableCell>
                      <TableCell align="right">{translate("financial_admin.volumetry.total")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rowsWithLight(email.rows).map((row) => (
                      <TableRow key={row.key}>
                        <TableCell sx={{ py: 0.75, bgcolor: `#${row.fill}`, color: `#${row.font}`, fontWeight: 700 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CategoryMedal name={row.plan} size={32} />
                            {row.plan}
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ py: 0.75, bgcolor: `#${row.fill}`, color: `#${row.font}`, fontWeight: 700 }}
                        >
                          {row.total}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Box>

          {dirty && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {translate("financial_admin.volumetry.unsaved")}
            </Typography>
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            <Button variant="outlined" disabled={busy || dirty} onClick={() => void refreshNumbers()}>
              {translate("financial_admin.volumetry.refresh")}
            </Button>
            <Button variant="contained" disabled={busy} onClick={() => void save()}>
              {translate("financial_admin.actions.save")}
            </Button>
            <Button
              variant="contained"
              disabled={busy || dirty}
              onClick={() => {
                if (parseEmailList(email.to_emails ?? "").length === 0) {
                  setError(translate("financial_admin.volumetry.recipient_required"));
                  return;
                }
                if (sharedEmails(email.to_emails ?? "", email.cc_emails ?? "").length > 0) {
                  setError(translate("financial_admin.volumetry.email_in_both"));
                  return;
                }
                setError("");
                setConfirmSend(true);
              }}
            >
              {translate("financial_admin.volumetry.send")}
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog
        open={confirmSend}
        onClose={() => {
          if (!sending) setConfirmSend(false);
        }}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          {translate("financial_admin.volumetry.send_confirm_title")}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, fontWeight: 400 }}>
            {translate("financial_admin.volumetry.send_confirm")}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <EmailSummary
            label={translate("financial_admin.volumetry.to")}
            value={email?.to_emails ?? ""}
            emptyLabel={translate("financial_admin.volumetry.email_empty")}
            tone="to"
          />
          <EmailSummary
            label={translate("financial_admin.volumetry.cc")}
            value={email?.cc_emails ?? ""}
            emptyLabel={translate("financial_admin.volumetry.email_empty")}
            tone="cc"
          />
          <Box sx={{ px: 1.5, py: 1.25, borderRadius: 2, bgcolor: "grey.50" }}>
            <Typography variant="caption" color="text.secondary">
              {translate("financial_admin.volumetry.subject")}
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25 }}>
              {email?.subject || "—"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button disabled={sending} onClick={() => setConfirmSend(false)}>
            {translate("financial_admin.actions.cancel")}
          </Button>
          <Button variant="contained" disabled={sending} onClick={() => void sendEmail()}>
            {translate("financial_admin.volumetry.confirm_send")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmReset}
        onClose={() => {
          if (!resetting) setConfirmReset(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{translate("financial_admin.volumetry.reset_confirm_title")}</DialogTitle>
        <DialogContent>
          <Typography>{translate("financial_admin.volumetry.reset_confirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={resetting} onClick={() => setConfirmReset(false)}>
            {translate("financial_admin.actions.cancel")}
          </Button>
          <Button color="warning" variant="contained" disabled={resetting} onClick={() => void resetEmail()}>
            {translate("financial_admin.volumetry.reset")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fileToDelete !== null} onClose={() => !fileBusy && setFileToDelete(null)} fullWidth maxWidth="xs">
        <DialogTitle>{translate("financial_admin.volumetry.remove_title")}</DialogTitle>
        <DialogContent>
          <Typography fontWeight={700}>{fileToDelete?.filename}</Typography>
          <Typography sx={{ mt: 1 }}>{translate("financial_admin.volumetry.remove_confirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={fileBusy} onClick={() => setFileToDelete(null)}>
            {translate("financial_admin.actions.cancel")}
          </Button>
          <Button color="error" variant="contained" disabled={fileBusy} onClick={() => void deleteFile()}>
            {translate("financial_admin.volumetry.remove")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={agreementHistoryOpen}
        onClose={() => setAgreementHistoryOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{translate("financial_admin.volumetry.agreement_history")}</DialogTitle>
        <DialogContent>
          {agreementHistoryLoading && (
            <Typography color="text.secondary">{translate("financial_admin.volumetry.history_loading")}</Typography>
          )}
          {agreementHistoryError && <Alert severity="error">{agreementHistoryError}</Alert>}
          {!agreementHistoryLoading && !agreementHistoryError && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {translate("financial_admin.volumetry.sent_count", {
                count: agreementHistory.filter((event) => event.action === "sent").length,
              })}
            </Typography>
          )}
          {!agreementHistoryLoading && !agreementHistoryError && agreementHistory.length === 0 && (
            <Typography color="text.secondary">{translate("financial_admin.volumetry.history_empty")}</Typography>
          )}
          {!agreementHistoryLoading && agreementHistory.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate("financial_admin.filters.year")}</TableCell>
                  <TableCell>{translate("financial_admin.filters.month")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry.action")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry.automatic")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry.actor")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry.when")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agreementHistory.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>{event.year}</TableCell>
                    <TableCell>{translate(`financial_admin.months.${event.month}`)}</TableCell>
                    <TableCell>{translate(`financial_admin.volumetry.actions.${event.action}`)}</TableCell>
                    <TableCell>
                      {event.automatic
                        ? translate("financial_admin.volumetry.automatic")
                        : translate("financial_admin.volumetry.manual")}
                    </TableCell>
                    <TableCell>{event.actor_name || "—"}</TableCell>
                    <TableCell>{new Date(event.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgreementHistoryOpen(false)}>{translate("financial_admin.actions.cancel")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{translate("financial_admin.volumetry.history_month")}</DialogTitle>
        <DialogContent>
          {historyLoading && (
            <Typography color="text.secondary">{translate("financial_admin.volumetry.history_loading")}</Typography>
          )}
          {historyError && <Alert severity="error">{historyError}</Alert>}
          {!historyLoading && !historyError && history.length === 0 && (
            <Typography color="text.secondary">{translate("financial_admin.volumetry.history_empty")}</Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
            {history.map((event) => (
              <Paper key={event.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Typography fontWeight={700}>
                  {translate(`financial_admin.volumetry.actions.${event.action}`)}
                  {event.automatic ? ` · ${translate("financial_admin.volumetry.automatic")}` : ""}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {event.actor_name || "—"} · {new Date(event.created_at).toLocaleString()}
                </Typography>
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{ mt: 1, mb: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}
                >
                  {JSON.stringify(event.payload ?? {}, null, 2)}
                </Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>{translate("financial_admin.actions.cancel")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function EmailListField({
  label,
  value,
  otherValue,
  otherMessage,
  onChange,
  tone,
}: {
  label: string;
  value: string;
  otherValue: string;
  otherMessage: string;
  onChange: (value: string) => void;
  tone: "to" | "cc";
}) {
  const translate = useTranslate();
  const emails = parseEmailList(value);
  const otherEmails = parseEmailList(otherValue);
  const [draft, setDraft] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [duplicate, setDuplicate] = useState<"same" | "other" | null>(null);

  const commit = (raw: string) => {
    const parts = raw
      .split(/[;,\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (parts.length === 0) {
      setDraft("");
      setInvalid(false);
      setDuplicate(null);
      return;
    }
    if (parts.some((item) => !EMAIL_PATTERN.test(item))) {
      setInvalid(true);
      setDuplicate(null);
      return;
    }
    const next = [...emails];
    let rejected: "same" | "other" | null = null;
    parts.forEach((item) => {
      const key = item.toLowerCase();
      if (next.some((email) => email.toLowerCase() === key)) {
        rejected = rejected === "other" ? rejected : "same";
        return;
      }
      if (otherEmails.some((email) => email.toLowerCase() === key)) {
        rejected = "other";
        return;
      }
      next.push(item);
    });
    if (next.length !== emails.length) onChange(joinEmailList(next));
    setDraft("");
    setInvalid(false);
    setDuplicate(rejected);
  };

  return (
    <Box>
      <Typography variant="subtitle2">{label}</Typography>
      <Box
        sx={{
          mt: 0.75,
          px: 1,
          py: 0.75,
          minHeight: 52,
          border: 1,
          borderColor: invalid || duplicate ? "error.main" : "divider",
          borderRadius: 2,
          bgcolor: "grey.50",
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          alignItems: "center",
          "&:focus-within": {
            borderColor: "primary.main",
            bgcolor: "background.paper",
          },
        }}
      >
        {emails.map((item) => (
          <Chip
            key={item}
            label={item}
            size="small"
            color={tone === "to" ? "primary" : "default"}
            sx={
              tone === "cc"
                ? {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                    color: "primary.dark",
                    "& .MuiChip-deleteIcon": {
                      color: "primary.main",
                      "&:hover": { color: "primary.dark" },
                    },
                  }
                : undefined
            }
            onDelete={() => onChange(joinEmailList(emails.filter((email) => email !== item)))}
          />
        ))}
        <TextField
          value={draft}
          placeholder={emails.length === 0 ? translate("financial_admin.volumetry.email_placeholder") : ""}
          variant="standard"
          onChange={(event) => {
            setInvalid(false);
            setDuplicate(null);
            setDraft(event.target.value);
          }}
          onBlur={() => {
            if (draft.trim()) commit(draft);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === "," || event.key === ";") {
              event.preventDefault();
              commit(draft);
            }
            if (event.key === "Backspace" && draft === "" && emails.length > 0) {
              onChange(joinEmailList(emails.slice(0, -1)));
            }
          }}
          onPaste={(event) => {
            const text = event.clipboardData.getData("text");
            if (!/[;,\n]/.test(text)) return;
            event.preventDefault();
            commit(`${draft}${text}`);
          }}
          slotProps={{ input: { disableUnderline: true } }}
          sx={{ flex: 1, minWidth: 220 }}
        />
      </Box>
      {invalid && (
        <Typography variant="caption" color="error">
          {translate("financial_admin.volumetry.email_invalid")}
        </Typography>
      )}
      {duplicate === "same" && (
        <Typography variant="caption" color="error">
          {translate("financial_admin.volumetry.email_duplicate")}
        </Typography>
      )}
      {duplicate === "other" && (
        <Typography variant="caption" color="error">
          {otherMessage}
        </Typography>
      )}
    </Box>
  );
}

function EmailSummary({
  label,
  value,
  emptyLabel,
  tone,
}: {
  label: string;
  value: string;
  emptyLabel: string;
  tone: "to" | "cc";
}) {
  const emails = parseEmailList(value);

  return (
    <Box sx={{ px: 1.5, py: 1.25, borderRadius: 2, bgcolor: "grey.50" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {emails.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {emptyLabel}
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
          {emails.map((item) => (
            <Chip
              key={item}
              label={item}
              size="small"
              color={tone === "to" ? "primary" : "default"}
              sx={
                tone === "cc"
                  ? {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                      color: "primary.dark",
                    }
                  : undefined
              }
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function EmailBodyPreview({
  body,
  tableTag,
  signatureTag,
  tableFile,
  extras,
}: {
  body: string;
  tableTag: string;
  signatureTag: string;
  tableFile?: VolumetryEmailFile;
  extras: VolumetryEmailFile[];
}) {
  const parts = splitBody(body, [tableTag, signatureTag]);
  const tableUrl = tableFile?.include && tableFile.url ? tableFile.url : null;
  const extraImages = extras.filter((file) => file.include && file.url);

  return (
    <Box sx={{ color: "inherit", fontSize: 14, lineHeight: 1.6 }}>
      {parts.map((part, index) => {
        if (part === tableTag) {
          if (!tableUrl) return null;
          return <PreviewImage key={`table-${index}`} src={tableUrl} alt="" maxWidth={400} />;
        }
        if (part === signatureTag) {
          return (
            <PreviewImage key={`signature-${index}`} src={SIGNATURE_SRC} alt="" maxWidth={280} />
          );
        }
        if (!part) return null;
        return (
          <Typography
            key={`text-${index}`}
            component="div"
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", color: "inherit" }}
          >
            {part}
          </Typography>
        );
      })}
      {extraImages.map((file) => (
        <PreviewImage key={file.id} src={file.url as string} alt={file.filename} maxWidth={400} />
      ))}
    </Box>
  );
}

function PreviewImage({ src, alt, maxWidth }: { src: string; alt: string; maxWidth: number }) {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{ display: "block", maxWidth, height: "auto", my: 1.5 }}
    />
  );
}

function FileColumn({
  title,
  hint,
  generated,
  extras,
  emptyLabel,
  replaceLabel,
  addLabel,
  replaceField,
  addField,
  replaceAccept,
  addAccept,
  disabled,
  onUpload,
  onInclude,
  onDelete,
}: {
  title: string;
  hint: string;
  generated?: VolumetryEmailFile;
  extras: VolumetryEmailFile[];
  emptyLabel: string;
  replaceLabel: string;
  addLabel: string;
  replaceField: UploadField;
  addField: UploadField;
  replaceAccept?: string;
  addAccept?: string;
  disabled: boolean;
  onUpload: (field: UploadField, list: FileList | null) => void;
  onInclude: (file: VolumetryEmailFile, include: boolean) => void;
  onDelete: (file: VolumetryEmailFile) => void;
}) {
  const translate = useTranslate();

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {hint}
      </Typography>
      {generated ? (
        <FileRow file={generated} disabled={disabled} onInclude={onInclude} onDelete={onDelete} />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {emptyLabel}
        </Typography>
      )}
      <Button component="label" size="small" variant="outlined" disabled={disabled} sx={{ mt: 1 }}>
        {replaceLabel}
        <input
          hidden
          type="file"
          accept={replaceAccept}
          onChange={(event) => {
            onUpload(replaceField, event.target.files);
            event.target.value = "";
          }}
        />
      </Button>
      {extras.length > 0 && (
        <Box sx={{ mt: 1.5, maxHeight: 220, overflow: "auto" }}>
          <Typography variant="caption" color="text.secondary">
            {translate("financial_admin.volumetry.extras")}
          </Typography>
          {extras.map((file) => (
            <FileRow key={file.id} file={file} disabled={disabled} onInclude={onInclude} onDelete={onDelete} />
          ))}
        </Box>
      )}
      <Button component="label" size="small" variant="outlined" disabled={disabled} sx={{ mt: 1 }}>
        {addLabel}
        <input
          hidden
          type="file"
          accept={addAccept}
          multiple
          onChange={(event) => {
            onUpload(addField, event.target.files);
            event.target.value = "";
          }}
        />
      </Button>
    </Paper>
  );
}

function FileRow({
  file,
  disabled,
  onInclude,
  onDelete,
}: {
  file: VolumetryEmailFile;
  disabled: boolean;
  onInclude: (file: VolumetryEmailFile, include: boolean) => void;
  onDelete: (file: VolumetryEmailFile) => void;
}) {
  const translate = useTranslate();
  const roleLabel = translate(`financial_admin.volumetry.roles.${file.role}`);

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", py: 0.5, opacity: file.include ? 1 : 0.5 }}>
      {file.kind === "image" && file.url && (
        <Box
          component="img"
          src={file.url}
          alt=""
          sx={{ width: 72, height: 48, objectFit: "cover", borderRadius: 1, border: 1, borderColor: "divider", flexShrink: 0 }}
        />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {file.url ? (
          <Typography
            component="a"
            href={file.url}
            target="_blank"
            rel="noreferrer"
            variant="body2"
            noWrap
            sx={{ display: "block", color: "text.primary" }}
          >
            {file.filename || "—"}
          </Typography>
        ) : (
          <Typography variant="body2" noWrap>
            {file.filename || "—"}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {roleLabel}
          {file.is_custom ? ` · ${translate("financial_admin.volumetry.custom")}` : ""}
        </Typography>
      </Box>
      <Switch
        size="small"
        checked={file.include}
        disabled={disabled}
        onChange={(_, checked) => onInclude(file, checked)}
        slotProps={{ input: { "aria-label": translate("financial_admin.volumetry.include_in_send") } }}
      />
      <IconButton size="small" disabled={disabled} aria-label={translate("financial_admin.volumetry.remove")} onClick={() => onDelete(file)}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
